import { Mutex } from 'async-mutex';

import { OnTextToSpeechChunkReceivedSubscriptionResult } from '../../requests/on-text-to-speech-chunk-received.susbscription.ts';

import { getAudioContextUtil } from './audio-context.util.ts';
import { AudioMediaSourceUtil } from './audio-media-source.util.ts';

export class TextToSpeechChunkProcessorUtil {
  private static _instance: TextToSpeechChunkProcessorUtil;
  private unprocessedChunks = new Map<
    string,
    Map<number, OnTextToSpeechChunkReceivedSubscriptionResult['onTextToSpeechChunkReceived']>
  >();
  private nextChunkNumbers = new Map<string, number>();
  private processingLocks = new Map<string, Mutex>();
  private mainLock = new Mutex();

  private constructor(private readonly audioContextUtil = getAudioContextUtil()) {}

  public static get instance(): TextToSpeechChunkProcessorUtil {
    if (!TextToSpeechChunkProcessorUtil._instance) {
      TextToSpeechChunkProcessorUtil._instance = new TextToSpeechChunkProcessorUtil();
    }
    return TextToSpeechChunkProcessorUtil._instance;
  }

  public async processChunk(
    subscription: { onClose: () => Promise<void> | void },
    audioMediaSourceUtil: AudioMediaSourceUtil,
    data: OnTextToSpeechChunkReceivedSubscriptionResult
  ): Promise<void> {
    if (!data.onTextToSpeechChunkReceived) return;

    const { id, order } = data.onTextToSpeechChunkReceived;

    const lock = await this.getLock(id);
    await lock.acquire();

    const pendingChunks = this.unprocessedChunks.get(id);
    let nextChunkNumber = this.nextChunkNumbers.get(id);

    if (!pendingChunks || nextChunkNumber === undefined) return;

    pendingChunks.set(order, data.onTextToSpeechChunkReceived);

    try {
      while (pendingChunks.has(nextChunkNumber)) {
        const chunk = pendingChunks.get(nextChunkNumber);
        if (!chunk) break;
        if (chunk.chunk) {
          const buffer = this.audioContextUtil.base64ToBytesArray(chunk.chunk);
          if (buffer) {
            audioMediaSourceUtil.appendBuffer(buffer);
          }
        }
        pendingChunks.delete(nextChunkNumber);
        nextChunkNumber++;
        this.nextChunkNumbers.set(id, nextChunkNumber);

        if (chunk.isComplete) {
          this.cleanupSubscriptionData(id, audioMediaSourceUtil, subscription);
        }
      }
    } catch {
      this.cleanupSubscriptionData(id, audioMediaSourceUtil, subscription);
    } finally {
      lock.release();
    }
  }

  private async getLock(id: string): Promise<Mutex> {
    await this.mainLock.acquire();
    try {
      const lock = this.processingLocks.get(id);
      if (lock) return lock;

      const newLock = new Mutex();
      this.processingLocks.set(id, newLock);
      return newLock;
    } finally {
      this.mainLock.release();
    }
  }

  public async initializeSubscriptionData(id: string): Promise<void> {
    await this.mainLock.acquire();
    try {
      this.unprocessedChunks.set(id, new Map());
      this.nextChunkNumbers.set(id, 0);
      this.processingLocks.set(id, new Mutex());
    } finally {
      this.mainLock.release();
    }
  }

  private async cleanupSubscriptionData(
    id: string,
    audioMediaSourceUtil: AudioMediaSourceUtil,
    subscription: { onClose: () => Promise<void> | void }
  ): Promise<void> {
    await this.mainLock.acquire();
    try {
      this.unprocessedChunks.delete(id);
      this.nextChunkNumbers.delete(id);
      this.processingLocks.delete(id);
      audioMediaSourceUtil.close();
      subscription.onClose();
    } finally {
      this.mainLock.release();
    }
  }
}

export const getTextToSpeechChunkProcessorUtil = (): TextToSpeechChunkProcessorUtil =>
  TextToSpeechChunkProcessorUtil.instance;
